import React, { createContext, useContext, useMemo, useState } from "react";
import { localLLM, LocalLLMStatus } from "@/llm/localTinyLlamaManager";
import { GenerateOptions } from "@/llm/tinyllamaWebGpuRuntime";

type LocalLLMContextValue = {
  status: LocalLLMStatus;
  isAvailable: boolean;
  generate: (options: GenerateOptions) => Promise<string>;
};

const LocalLLMContext = createContext<LocalLLMContextValue | undefined>(undefined);

type Props = {
  children: React.ReactNode;
};

export const LocalLLMProvider: React.FC<Props> = ({ children }) => {
  // The in-browser MLC web-LLM (Llama 1B downloaded from canxp.ai) was the
  // backing model for the legacy MaplePT preview chat. Fine-tune chat now
  // runs through RunPod Serverless, so we no longer auto-download megabytes
  // of weights on every page load. Consumers that still read this context
  // see a permanent "idle" / `isAvailable=false` state and degrade
  // gracefully (their UI just doesn't expose the "local" model option).
  const [status] = useState<LocalLLMStatus>({ state: "idle" });

  const value = useMemo<LocalLLMContextValue>(() => ({
    status,
    isAvailable: status.state === "ready",
    generate: (opts) => localLLM.generate(opts),
  }), [status]);

  return <LocalLLMContext.Provider value={value}>{children}</LocalLLMContext.Provider>;
};

export const useLocalLLM = (): LocalLLMContextValue => {
  const ctx = useContext(LocalLLMContext);
  if (ctx) return ctx;
  // Safe fallback to avoid crashing if provider is missing in a sub-root
  return {
    status: { state: "webgpu_not_supported" },
    isAvailable: false,
    generate: async () => { throw new Error("Local LLM not initialized"); },
  };
};